// 2026-10-22 Tickets page "By inspection" view: helpers for GET Tickets?includeInspections=true
// (backend TicketInspectionSummaryService). Pure functions - unit tested in ticket-inspection-utils.test.js.

export const TICKET_VIEW_STORAGE_KEY = 'eauditor.ticketsView'
export const TICKET_VIEWS = { group: 'group', list: 'list' }

const OPEN_STATUS_IDS = [1, 2, 3, 6, 7]
const DONE_STATUS_IDS = [4, 5, 9]

export const isOpenTicketStatus = statusId =>
    OPEN_STATUS_IDS.includes(Number(statusId))
export const isDoneTicketStatus = statusId =>
    DONE_STATUS_IDS.includes(Number(statusId))

export const readTicketView = () => {
    try {
        const v = window.localStorage.getItem(TICKET_VIEW_STORAGE_KEY)
        return v === TICKET_VIEWS.list ? TICKET_VIEWS.list : TICKET_VIEWS.group
    } catch {
        return TICKET_VIEWS.group
    }
}

export const saveTicketView = view => {
    try {
        window.localStorage.setItem(TICKET_VIEW_STORAGE_KEY, view)
    } catch {
        // private window / blocked storage: the view simply is not remembered
    }
}

const pick = (obj, ...keys) => {
    const key = keys.find(
        k => obj && obj[k] !== undefined && obj[k] !== null,
    )
    return key === undefined ? undefined : obj[key]
}

export const normalizeTicketInspections = response => {
    const raw = response?.data && !Array.isArray(response.data) ? response.data : response
    const list = pick(raw || {}, 'inspections', 'Inspections')
    const names = pick(raw || {}, 'assigneeNames', 'AssigneeNames') || {}
    const byAudit = {}
    ;(Array.isArray(list) ? list : []).forEach(i => {
        const auditId = Number(pick(i, 'AuditId', 'auditId'))
        if (!auditId) return
        byAudit[auditId] = {
            auditId,
            auditGuid: pick(i, 'AuditGuid', 'auditGuid') || '',
            auditName: pick(i, 'AuditName', 'auditName') || '',
            templateName: pick(i, 'TemplateName', 'templateName') || '',
            auditStatus: Number(pick(i, 'AuditStatus', 'auditStatus') || 0),
            completedOn: pick(i, 'CompletedOn', 'completedOn') || null,
            inspectorName: pick(i, 'InspectorName', 'inspectorName') || '',
            locationId: pick(i, 'LocationId', 'locationId') || null,
            locationName: pick(i, 'LocationName', 'locationName') || '',
            findingsTotal: Number(pick(i, 'FindingsTotal', 'findingsTotal') || 0),
            findingsOpen: Number(pick(i, 'FindingsOpen', 'findingsOpen') || 0),
            findingsDone: Number(pick(i, 'FindingsDone', 'findingsDone') || 0),
            findingsHeld: Number(pick(i, 'FindingsHeld', 'findingsHeld') || 0),
            completedReportId: pick(i, 'CompletedReportId', 'completedReportId') || null,
            approvalState: pick(i, 'ApprovalState', 'approvalState') || 'none',
            approvalWaitingOn: pick(i, 'ApprovalWaitingOn', 'approvalWaitingOn') || '',
        }
    })
    const assigneeNames = {}
    Object.keys(names).forEach(k => {
        assigneeNames[String(k)] = names[k]
    })
    return { byAudit, assigneeNames, available: Array.isArray(list) }
}

// Chip for the inspection header; null = no approval workflow on this inspection.
export const approvalChip = inspection => {
    if (!inspection) return null
    const open = inspection.findingsOpen
    switch (inspection.approvalState) {
        case 'waiting_findings':
            return {
                tone: 'wait',
                text: `Approval waits - ${open} finding${open === 1 ? '' : 's'} open`,
            }
        case 'pending':
            return {
                tone: 'sent',
                text: inspection.approvalWaitingOn
                    ? `With approver - ${inspection.approvalWaitingOn}`
                    : 'With approver',
            }
        case 'approved':
            return { tone: 'ok', text: 'Report approved' }
        case 'rejected':
            return { tone: 'bad', text: 'Report rejected' }
        case 'not_started':
            return inspection.auditStatus === 5
                ? { tone: 'muted', text: 'Approval not started' }
                : { tone: 'muted', text: 'Inspection not completed' }
        default:
            return null
    }
}

export const assigneeLabel = (ticket, assigneeNames = {}) => {
    if (Number(ticket?.assignedToTypeId) === 1) {
        return assigneeNames[String(ticket.assignedToId)] || 'User'
    }
    if (Number(ticket?.assignedToTypeId) === 2) return 'Team'
    return '—'
}

const ts = value => {
    const t = value ? new Date(value).getTime() : NaN
    return Number.isFinite(t) ? t : 0
}

// Groups the page's tickets by inspection (audit). Order: newest completed inspection first (2026-10-27, user:
// the latest inspection showed last), by the inspection's completion time, else its newest finding.
// Tickets without an audit go to one "No inspection" group at the end.
export const groupTicketsByInspection = (tickets = [], byAudit = {}) => {
    const groups = new Map()
    tickets.forEach(ticket => {
        const auditId = Number(ticket.auditId) || 0
        if (!groups.has(auditId)) {
            const summary = byAudit[auditId] || null
            groups.set(auditId, {
                auditId,
                summary,
                name: auditId
                    ? summary?.auditName ||
                      ticket.auditName ||
                      `Inspection #${auditId}`
                    : 'No inspection',
                templateName: summary?.templateName || ticket.templateName || '',
                tickets: [],
            })
        }
        groups.get(auditId).tickets.push(ticket)
    })
    const list = Array.from(groups.values()).map(g => {
        const open = g.tickets.filter(t => isOpenTicketStatus(t.statusId))
        const newest = Math.max(...g.tickets.map(t => ts(t.createdOn)))
        return {
            ...g,
            oldestOpen: open.length ? Math.min(...open.map(t => ts(t.createdOn))) : null,
            newest,
            sortTime: ts(g.summary?.completedOn) || newest,
        }
    })
    list.sort((a, b) => {
        if (!a.auditId !== !b.auditId) return a.auditId ? -1 : 1
        if (b.sortTime !== a.sortTime) return b.sortTime - a.sortTime
        return b.auditId - a.auditId
    })
    return list
}

// List view: keep the rows of one inspection together, in the order the server returned them (newest first).
export const orderTicketsByInspection = (tickets = []) => {
    const firstIndex = new Map()
    tickets.forEach((t, i) => {
        const key = Number(t.auditId) || 0
        if (!firstIndex.has(key)) firstIndex.set(key, i)
    })
    return tickets
        .map((t, i) => ({ t, i }))
        .sort((a, b) => {
            const ga = firstIndex.get(Number(a.t.auditId) || 0)
            const gb = firstIndex.get(Number(b.t.auditId) || 0)
            return ga !== gb ? ga - gb : a.i - b.i
        })
        .map(x => x.t)
}

// Progress of one inspection: from the server summary (all its findings) when present, else from the page rows.
export const inspectionProgress = group => {
    const s = group?.summary
    if (s && s.findingsTotal > 0) {
        return { done: s.findingsDone, total: s.findingsTotal }
    }
    const rows = group?.tickets || []
    return {
        done: rows.filter(t => isDoneTicketStatus(t.statusId)).length,
        total: rows.length,
    }
}
